import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataContext } from "../dataContext";
import TransferDetails from "../components/TransferDetails";
import BottomButton from "../components/BottomButton";
import SendSms from "../components/SendSMS";
import { ReactComponent as Copy } from "../assets/ico_copy.svg";
import { ReactComponent as Info } from "../assets/ico_info.svg";
import { ROUTE_CONSTANTS } from "../constants/routeConstants";
import useApi from "../api/api";
import { SpinnerCircularFixed } from "spinners-react";
import { isSMSCapable } from "../utils/utils";
import { useTransferApi } from "../api/transferApi";
import { isMobileDevice } from "../utils/deviceDetection";
import StepUpAuthModal from "../components/StepUpAuthModal";

const { OUTGOING_DETAILS } = ROUTE_CONSTANTS;

const OutgoingDetails = ({ branding, homePath }) => {
  const api = useApi(false);
  const urlParams = useParams();
  const navigate = useNavigate();
  const [cancelStatus, setCancelStatus] = useState(false);
  const [resendData, setResendData] = useState({});
  const [transfer, setTransfer] = useState(null);
  const [stepUpAuthRequired, setStepUpAuthRequired] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const { history, getHistory, notificationFeed } = useContext(DataContext);
  const { getTransferStatus, cancelRequest } = useTransferApi();
  
  // Still use notification feed for off-network shortlinks
  const notification = notificationFeed.find(
    ({ transfer_id }) => transfer_id === urlParams.id
    // // notification/feed doesn't work locally, uncomment an option below
    // ({ transfer_id }) => transfer_id === "9754335f-7db7-5f10-9db0-79fd5b6fcb02" // ON NETWORK FAILED
    // ({ transfer_id }) => transfer_id === "17bfb8a4-2fa3-5615-b4b4-e3cc6151c65d" // ON NETWORK SUCCEEDED
    // ({ transfer_id }) => transfer_id === "c35bc840-1865-5bf5-916d-b85ce20589c1" // OFF NETWORK PENDING
  );

  // // for dummy ON NETWORK
  // if (transfer) {
  //   transfer.note = "Dummy data";
  //   transfer.status = notification?.status?.toUpperCase();
  // }

  // Fetch transfer data using the new endpoint
  useEffect(() => {
    const fetchTransferData = async () => {
      try {
        setLoading(true);
        const transferData = await getTransferStatus(urlParams.id);
        console.log("Transfer data from new API:", transferData);
        
        setTransfer(transferData);
        
        // Check for step-up auth events
        const isPending = transferData.status === 'pending' || transferData.status === 'PENDING';
        const events = transferData.events || transferData.getstream_events || [];
        
        if (isPending && events.length > 0) {
          const stepUpEvent = events.find(
            event => event.event === 'step_up_auth_needed'
          );
          
          // Check if shortlink_generated event exists (indicating step-up was completed)
          const shortlinkEvent = events.find(
            event => event.event === 'shortlink_generated'
          );
          
          // Only show step-up auth if it's needed AND shortlink hasn't been generated
          if (stepUpEvent && !shortlinkEvent) {
            const verificationUrl = stepUpEvent.verification_url || 
                                   stepUpEvent.data?.verification_url ||
                                   stepUpEvent.url ||
                                   stepUpEvent.data?.url;
            
            if (verificationUrl) {
              console.log("Step-up auth required with URL:", verificationUrl);
              setStepUpAuthRequired(true);
              setVerificationUrl(verificationUrl);
              
              // Show QR code modal for desktop
              if (!isMobileDevice()) {
                setShowQrModal(true);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching transfer data:", error);
        // Fallback to history data if new endpoint fails
        const historyTransfer = history.find(({ id }) => id === urlParams.id);
        if (historyTransfer) {
          setTransfer(historyTransfer);
        }
      } finally {
        setLoading(false);
      }
    };

    if (urlParams.id) {
      fetchTransferData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.id]); // Only depend on URL param to prevent loops

  window.scrollTo(0, 0);
  const canSendSMS = isSMSCapable();

  const shouldShowSmsSend = () => {
    if (transfer !== undefined) {
      return (
        transfer?.recipient?.type === "phone" &&
        canSendSMS &&
        notification?.shortlink
      );
    }
    return false;
  };

  useEffect(() => {
    if (transfer && notification) {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding?.sms_customization?.off_net_first || "I am sending you"} $${transfer?.amount?.value} ${branding?.sms_customization?.off_net_second || "from my account at"} ${notification?.financial_institution || ""} ${branding?.sms_customization?.off_net_third || "with the note"} "${transfer?.note || ""}". ${branding?.sms_customization?.off_net_fourth || "Click this link to receive your funds:"} ${notification?.shortlink}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding.sms_customization, notification?.shortlink, notification?.financial_institution, transfer?.recipient?.value, transfer?.amount?.value, transfer?.note]); // Use specific fields instead of whole objects

  const cancelTransfer = async () => {
    setResendData({
      ...resendData,
      disabled: true,
    });
    navigate(
      `${OUTGOING_DETAILS.replace(":id", transfer.id).replace(":action", "cancel")}`
    );
    try {
      // Request-for-Pay rows the requester created cancel via the dedicated RfP
      // endpoint; normal sends use the transfer-outgoing delete route.
      if (transfer.originated_as_rfp === true) {
        await cancelRequest(transfer.id);
      } else {
        await api.delete(`/transfer/outgoing/${transfer.id}`);
      }
      setCancelStatus("STARTED");
    } catch (error) {
      console.error("cancel transfer error", error);
      setCancelStatus("ERROR");
    }
  };

  useEffect(() => {
    if (cancelStatus === "STARTED" && transfer?.status === "PENDING") {
      const interval = setInterval(async () => {
        // Re-fetch the transfer itself so the local status reflects the
        // backend's terminal state once the cancel job processes (PENDING →
        // CANCELLED for RfP, or FAILED for a normal send). Without this the
        // screen never sees the state change and always hits the timeout.
        try {
          const updated = await getTransferStatus(urlParams.id);
          if (updated) setTransfer(updated);
        } catch (err) {
          // Transient fetch error — keep polling until the timeout fires.
        }
        getHistory();
      }, 3000);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setCancelStatus("TIMEDOUT");
      }, 20000);
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelStatus, getHistory, transfer?.status]); // Only depend on transfer.status, not the whole object

  const clickCopy = () => {
    navigator.clipboard.writeText(resendData.message);
    document.getElementById("show_click_copy").style.display = "inline";
    setTimeout(() => {
      document.getElementById("show_click_copy").style.display = "none";
    }, 1000);
  };

  const renderCancelled = () => {
    const isRfp = transfer.originated_as_rfp === true;
    const noun = isRfp ? "request" : "transfer";
    if (cancelStatus === "TIMEDOUT" && transfer.status === "PENDING") {
      return (
        <h2>
          Your {noun} cancellation is still processing. Please check the
          status in the 'History' tab later.
        </h2>
      );
    }
    if (cancelStatus === "ERROR") {
      return (
        <h2>
          {isRfp ? "Request" : "Transfer"} cancel failed. Please contact your
          bank for more info.
        </h2>
      );
    }
    if (transfer.status === "FAILED" || transfer.status === "CANCELLED") {
      return <h2>Your {noun} has been cancelled.</h2>;
    }
    return (
      <>
        <h1 style={{ marginBottom: "40px" }}>
          {isRfp ? "Cancelling Request" : "Cancelling Transfer"}
        </h1>
        <SpinnerCircularFixed
          color={branding.sending_colors.sending_bg_secondary}
          secondaryColor={branding.sending_colors.sending_bg_primary}
          speed={150}
          style={{ height: "60px", width: "60px" }}
        />
      </>
    );
  };

  if (loading) {
    return (
      <main className="page_with_nav">
        <div className="center_page">
          <SpinnerCircularFixed
            color={branding.sending_colors.sending_bg_secondary}
            secondaryColor={branding.sending_colors.sending_bg_primary}
            speed={150}
            style={{ height: "60px", width: "60px" }}
          />
        </div>
      </main>
    );
  }

  return (
    <main className={cancelStatus ? "page_without_nav" : "page_with_nav"}>
      {transfer && transfer.recipient ? (
        <>
          {cancelStatus ? (
            <div
              className="center_page"
              style={{
                flexDirection: "column",
                height: "70%",
                textAlign: "center",
              }}
            >
              {renderCancelled()}
            </div>
          ) : (
            <div>
              {transfer.status !== "PENDING" ? (
                <>
                  <h3 className="status">
                    {transfer.status === "SUCCEEDED" ? (
                      transfer.originated_as_rfp === true ? (
                        <>Payment received</>
                      ) : (
                        <>Payment was sent</>
                      )
                    ) : transfer.originated_as_rfp === true ? (
                      <>Payment request failed</>
                    ) : (
                      <>Payment failed</>
                    )}
                  </h3>
                  <hr style={{ margin: "20px 0" }} />
                </>
              ) : (
                <div style={{ marginBottom: "40px" }}>
                  <h3 className="status">
                    {stepUpAuthRequired ? (
                      <>
                        {isMobileDevice() ? (
                          <>Additional authentication required</>
                        ) : (
                          <>Your transfer is pending authentication</>
                        )}
                      </>
                    ) : transfer.originated_as_rfp === true ? (
                      <>
                        Your payment request is still awaiting payment.
                        {transfer.is_cancellable && (
                          <>
                            {" "}
                            The requestee needs to complete the payment.
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        Your transfer is still processing.
                        {transfer.is_cancellable && (
                          <>
                            {" "}
                            The recipient needs to take action to receive the funds.
                          </>
                        )}
                      </>
                    )}
                  </h3>
                  {stepUpAuthRequired && isMobileDevice() && (
                    <div style={{ marginTop: "20px", marginBottom: "20px", padding: "15px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                      <p style={{ marginBottom: "10px", fontSize: "14px" }}>
                        This transfer requires step-up authentication from your financial institution.
                      </p>
                      <button
                        onClick={() => window.open(verificationUrl, '_blank')}
                        style={{ 
                          display: "block", 
                          width: "100%",
                          padding: "12px 24px", 
                          backgroundColor: "#007bff", 
                          color: "#ffffff", 
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "16px",
                          fontWeight: "bold",
                          cursor: "pointer",
                          marginTop: "10px"
                        }}
                      >
                        Complete Authentication
                      </button>
                    </div>
                  )}
                  {transfer.is_cancellable && (
                    <>
                      {shouldShowSmsSend() && (
                        <div className="transfer_action_info">
                          <div style={{ marginRight: "10px" }}>
                            <Info className="fill_sending_bg_primary stroke_sending_bg_secondary" />
                          </div>
                          <>
                            You can click the button below to message the
                            recipient. It includes the link required to receive
                            the funds.
                          </>
                        </div>
                      )}
                      <hr style={{ margin: "20px 0" }} />
                      {shouldShowSmsSend() ? (
                        <SendSms {...resendData} disableAndroidSms={branding?.disable_android_sms}></SendSms>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {notification?.shortlink && (
                            <div className="np-message-card">
                              <span>
                                {resendData.message?.split("http")[0]}
                                <strong>{notification.shortlink}</strong>
                              </span>
                              <div style={{ textAlign: "right" }}>
                                <Copy
                                  className="fill_sending_bg_tertiary stroke_sending_bg_secondary"
                                  onClick={() => clickCopy()}
                                  style={{ cursor: "pointer" }}
                                />
                                <div
                                  id="show_click_copy"
                                  style={{ display: "none" }}
                                >
                                  Message copied
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        className="np-btn-secondary"
                        style={{ marginTop: "15px", color: "var(--danger)" }}
                        onClick={cancelTransfer}
                      >
                        {transfer.originated_as_rfp === true
                          ? "Cancel Request"
                          : "Cancel Transfer"}
                      </button>
                    </>
                  )}
                </div>
              )}
              {TransferDetails(transfer)}
            </div>
          )}
        </>
      ) : (
        <div className="center_page">Transfer Not Found</div>
      )}
      {!(cancelStatus === "STARTED" && transfer.status === "PENDING") &&
        BottomButton(() => navigate(homePath), "Return home")}
      
      {/* Step-up Auth Modal */}
      {showQrModal && verificationUrl && (
        <StepUpAuthModal
          verificationUrl={verificationUrl}
          onClose={() => {
            setShowQrModal(false);
            // Just close the modal, stay on the same page
          }}
          onRefresh={() => {
            // Refresh just re-renders the QR code
            setShowQrModal(false);
            setTimeout(() => setShowQrModal(true), 100);
          }}
        />
      )}
    </main>
  );
};

export default OutgoingDetails;
